import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/site/PageHero";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";
import { Button } from "@/components/ui/button";
import { Clock, MapPin, Users, Hotel, Car, Info, Sparkles, Route, Moon } from "lucide-react";
import { RichHtml } from "@/components/admin/RichHtml";

const htmlToText = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").replace(/\s+/g, " ").trim();
};

export default function ItineraryDetail() {
  const { slug } = useParams();
  const [it, setIt] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setIt(null);
    api.get(`/itineraries/${slug}`).then((r) => setIt(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: it ? `${it.title} — Sample Itinerary | Maha Sampada` : "Itinerary | Maha Sampada",
    description: it ? `${it.title}: ${htmlToText(it.snapshot)} Fully customizable Maharashtra itinerary for travel partners.` : "",
  });

  if (notFound) return <NotFoundBlock title="Itinerary Not Found" message="This sample itinerary isn't available. Browse our full itinerary library instead." backTo="/itineraries" backLabel="View All Itineraries" />;
  if (!it) return <div className="flex min-h-[60vh] items-center justify-center pt-20 font-accent">Loading…</div>;

  const snapshotText = htmlToText(it.snapshot);

  return (
    <div>
      <PageHero overline={it.theme} title={it.title} subtitle={snapshotText} image={it.cover_image}
        crumbs={[{ label: "Sample Itineraries", to: "/itineraries" }, { label: it.title }]} height="h-[56vh]" />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        {/* Customization banner */}
        <div data-testid="customization-banner" className="mb-10 flex items-start gap-3 rounded-2xl border border-saffron/30 bg-saffron/5 p-5">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
          <p className="text-sm text-charcoal">
            <strong>This is a sample itinerary.</strong> Routing, hotels, experiences and duration can be customized according to your client's requirements.
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* Snapshot chips */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Duration", it.duration, Clock], ["Region", (it.regions && it.regions.join(", ")) || it.region_name, MapPin], ["Best For", Array.isArray(it.best_for) ? it.best_for.join(", ") : it.best_for, Users], ["Theme", Array.isArray(it.themes) && it.themes.length ? it.themes.join(", ") : it.theme, Sparkles]].map(([label, val, Icon]) => (
                <div key={label} className="rounded-xl border border-gold/25 bg-white p-4">
                  <Icon className="h-4 w-4 text-saffron" />
                  <div className="mt-2 overline text-[10px] text-muted-foreground">{label}</div>
                  <div className="mt-1 text-sm font-semibold text-charcoal">{val || "—"}</div>
                </div>
              ))}
            </div>

            {(it.destinations || []).length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-charcoal">Destinations Covered</h2>
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="destinations-list">{(it.destinations || []).map((d, i) => {
                  const name = typeof d === "string" ? d : d.name;
                  return <span key={i} className="flex items-center gap-2 font-accent text-xs text-charcoal">{i > 0 && <span className="text-saffron">→</span>}<span className="rounded-full border border-gold/30 bg-white px-4 py-1.5">{name}</span></span>;
                })}</div>
              </div>
            )}

            {htmlToText(it.why) && (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-charcoal">Why This Trip</h2>
                <RichHtml html={it.why} testid="itin-why" className="mt-3 text-base leading-relaxed text-muted-foreground" />
              </div>
            )}

            {/* Day-wise */}
            <div className="mt-10">
              <h2 className="font-display text-2xl font-semibold text-charcoal">Day-wise Itinerary</h2>
              <div className="mt-6 space-y-4">
                {it.days?.map((day) => {
                  const routeNames = [day.start_place, ...(day.places_visited || []), day.end_place].filter((p) => p && !p.none).map((p) => (typeof p === "string" ? p : p.name)).filter((n, k, a) => n && n !== a[k - 1]);
                  return (
                  <div key={day.day} data-testid={`day-${day.day}`} className="relative rounded-2xl border border-gold/25 bg-white p-6 pl-16">
                    <div className="absolute left-5 top-6 flex h-8 w-8 items-center justify-center rounded-full bg-maroon font-accent text-xs font-bold text-cream">{day.day}</div>
                    <h3 className="font-display text-lg font-semibold text-charcoal">{day.title}</h3>
                    {routeNames.length > 0 && <div className="mt-1 flex items-center gap-1 text-xs text-saffron"><Route className="h-3.5 w-3.5" />{routeNames.join(" → ")}{day.distance ? ` · ${day.distance}` : ""}{day.travel_time ? ` · ${day.travel_time}` : ""}</div>}
                    <RichHtml html={day.detail} className="mt-2 text-sm text-muted-foreground" />
                    {(day.sightseeing || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{day.sightseeing.map((s, k) => <span key={k} className="rounded-full bg-cream px-3 py-0.5 text-[11px] text-charcoal">{s}</span>)}</div>}
                    {day.overnight_place && !day.overnight_place.none && <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Moon className="h-3.5 w-3.5 text-gold" />Overnight: {typeof day.overnight_place === "string" ? day.overnight_place : day.overnight_place.name}</div>}
                  </div>
                ); })}
              </div>
            </div>

            {/* Recommendations */}
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-gold/25 bg-white p-6">
                <div className="flex items-center gap-2 font-display text-lg font-semibold text-charcoal"><Hotel className="h-5 w-5 text-saffron" />Suggested Hotel Category</div>
                <p className="mt-2 text-sm text-muted-foreground">{it.hotel_category}</p>
              </div>
              <div className="rounded-2xl border border-gold/25 bg-white p-6">
                <div className="flex items-center gap-2 font-display text-lg font-semibold text-charcoal"><Car className="h-5 w-5 text-saffron" />Transportation</div>
                <p className="mt-2 text-sm text-muted-foreground">{it.transport}</p>
              </div>
            </div>

            {(it.accommodation || []).length > 0 && (
              <div className="mt-10" data-testid="accommodation-plan">
                <h2 className="font-display text-2xl font-semibold text-charcoal">Accommodation Plan</h2>
                <div className="mt-4 overflow-x-auto rounded-2xl border border-gold/25">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/60 text-left"><tr>
                      <th className="p-3 font-accent text-xs uppercase text-muted-foreground">Place</th>
                      <th className="p-3 font-accent text-xs uppercase text-muted-foreground">Nights</th>
                      <th className="p-3 font-accent text-xs uppercase text-muted-foreground">Category</th>
                      <th className="p-3 font-accent text-xs uppercase text-muted-foreground">Meal Plan</th>
                    </tr></thead>
                    <tbody>
                      {it.accommodation.map((r, i) => (
                        <tr key={i} className="border-t border-gold/15" data-testid={`accom-public-${i}`}>
                          <td className="p-3 font-medium text-charcoal">{r.place?.name || "—"}{r.properties?.length ? <div className="mt-0.5 text-[11px] font-normal text-muted-foreground">{r.properties.join(", ")}</div> : null}</td>
                          <td className="p-3">{r.nights}</td>
                          <td className="p-3">{r.category}</td>
                          <td className="p-3">{r.meal_plan}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {it.accommodation.some((r) => r.properties?.length) && <p className="mt-2 text-xs text-muted-foreground">Suggested properties are indicative and subject to availability. Final hotels will be confirmed at the quotation or booking stage.</p>}
              </div>
            )}

            {/* Inclusions / Exclusions */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-forest/20 bg-forest/5 p-6">
                <h3 className="font-display text-lg font-semibold text-forest">General Inclusions</h3>
                <RichHtml html={it.inclusions} testid="itin-inclusions" className="mt-3 text-sm text-charcoal" />
              </div>
              <div className="rounded-2xl border border-maroon/20 bg-maroon/5 p-6">
                <h3 className="font-display text-lg font-semibold text-maroon">General Exclusions</h3>
                <RichHtml html={it.exclusions} testid="itin-exclusions" className="mt-3 text-sm text-charcoal" />
              </div>
            </div>

            {htmlToText(it.notes) && (
              <div className="mt-6 rounded-2xl border border-gold/25 bg-white p-6">
                <h3 className="font-display text-lg font-semibold text-charcoal">Important Notes</h3>
                <RichHtml html={it.notes} testid="itin-notes" className="mt-3 text-sm text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Sidebar quote */}
          <aside>
            <div className="sticky top-24 rounded-2xl border border-gold/25 bg-white p-7">
              <h3 className="font-display text-xl font-semibold text-charcoal">Request B2B Quote</h3>
              <p className="mt-2 text-sm text-muted-foreground">Get confidential B2B pricing and a tailored version of this journey for your client.</p>
              <Link to={`/enquiry?ref=${encodeURIComponent(it.title)}`} className="mt-5 block">
                <Button data-testid="itinerary-quote-cta" className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">Request B2B Quote</Button>
              </Link>
              <Link to="/itineraries" className="mt-3 block text-center font-accent text-xs text-saffron hover:underline">← Back to all itineraries</Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
